/**
 * BrandHub Index Page
 * Features an interactive orbit visualization showcasing demo brands, products, and events
 */

import { useMemo, useEffect, useCallback, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Building2, Package, Calendar, Rocket, Play, HelpCircle } from 'lucide-react';
import { GlitchText } from '@/components/ui/GlitchText';
import { HeroOrbit } from '@/components/landing/HeroOrbit';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ParallaxCard } from '@/components/ui/parallax-card';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/contexts/AuthContext';
import { DEMO_GRADIENTS, DEMO_INDUSTRIES, DEMO_CARD_IMAGES, getOrbitBrands, getOrbitProducts, getOrbitEvents } from '@/data/demoGuides';
import { BrandHubLogo } from '@/components/BrandHubLogo';
import { DemoFirstTimeModal, useFirstTimeDemoPrompt } from '@/components/landing/DemoFirstTimeModal';
import brandhubLogo from '@/assets/brandhub-logo.svg';

// Lazy load below-fold and non-LCP-critical components to improve LCP
const ParticleEmbers = lazy(() => import('@/components/ParticleEmbers').then(m => ({ default: m.ParticleEmbers })));
const InteractiveCTA = lazy(() => import('@/components/landing/InteractiveCTA').then(m => ({ default: m.InteractiveCTA })));
const FeaturesShowcase = lazy(() => import('@/components/landing/FeaturesShowcase').then(m => ({ default: m.FeaturesShowcase })));
const GetStartedSurveyModal = lazy(() => import('@/components/landing/GetStartedSurveyModal').then(m => ({ default: m.GetStartedSurveyModal })));

const Index = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isApproved, accessStatus, isLoading } = useAuth();
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [pendingDemo, setPendingDemo] = useState<{ path: string; name: string } | null>(null);
  const { shouldShowPrompt } = useFirstTimeDemoPrompt();

  // Auto-redirect authenticated users to dashboard
  useEffect(() => {
    if (isLoading) return; // Wait for auth to settle
    const hasAccess = !!user && accessStatus === 'ready' && (isAdmin || isApproved);
    if (hasAccess) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, accessStatus, isAdmin, isApproved, isLoading, navigate]);

  // Get orbit entities (lightweight stubs for visualization)
  const orbitBrands = useMemo(() => getOrbitBrands(), []);
  const orbitProducts = useMemo(() => getOrbitProducts(), []);
  const orbitEvents = useMemo(() => getOrbitEvents(), []);

  // Featured demos for cards below
  const featuredDemos = [
    {
      id: 'demo-brandhub',
      name: 'BrandHub',
      type: 'brand',
      description: 'The platform itself - explore how BrandHub documents its own brand identity',
      gradient: DEMO_GRADIENTS['demo-brandhub'],
      industry: DEMO_INDUSTRIES['demo-brandhub'],
      cardImage: DEMO_CARD_IMAGES['demo-brandhub'],
      icon: Rocket,
      path: '/demo/brand/brandhub',
    },
    {
      id: 'demo-nexus-tech',
      name: 'Nexus Tech',
      type: 'brand',
      description: 'A modern tech company brand guide with comprehensive identity system',
      gradient: DEMO_GRADIENTS['demo-nexus-tech'],
      industry: DEMO_INDUSTRIES['demo-nexus-tech'],
      cardImage: DEMO_CARD_IMAGES['demo-nexus-tech'],
      icon: Building2,
      path: '/demo/brand/demo-nexus-tech',
    },
    {
      id: 'demo-nexus-cloud',
      name: 'Nexus Cloud Platform',
      type: 'product',
      description: 'Enterprise cloud product with complete visual guidelines',
      gradient: DEMO_GRADIENTS['demo-nexus-cloud'],
      industry: DEMO_INDUSTRIES['demo-nexus-cloud'],
      cardImage: DEMO_CARD_IMAGES['demo-nexus-cloud'],
      icon: Package,
      path: '/demo/product/demo-nexus-cloud',
    },
    {
      id: 'demo-innovation-summit',
      name: 'Innovation Summit 2026',
      type: 'event',
      description: 'Conference event kit with signage, banners, and digital assets',
      gradient: DEMO_GRADIENTS['demo-innovation-summit'],
      industry: DEMO_INDUSTRIES['demo-innovation-summit'],
      cardImage: DEMO_CARD_IMAGES['demo-innovation-summit'],
      icon: Calendar,
      path: '/demo/event/demo-innovation-summit',
    },
  ];

  const handleSignInClick = useCallback(() => {
    // If the user is already authenticated + approved, don't send them to /auth only to immediately redirect back to /.
    // This was perceived as “Sign In does nothing”.
    const hasAccess = !!user && accessStatus === 'ready' && (isAdmin || isApproved);
    navigate(hasAccess ? '/dashboard' : '/auth');
  }, [user, accessStatus, isAdmin, isApproved, navigate]);

  // Handle demo click - show first-time modal or navigate directly
  const handleDemoClick = useCallback((path: string, name: string) => {
    if (shouldShowPrompt) {
      setPendingDemo({ path, name });
      setShowDemoModal(true);
    } else {
      navigate(path);
    }
  }, [shouldShowPrompt, navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <BrandHubLogo size="md" />
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Button onClick={handleSignInClick} variant="outline" size="sm">
              Sign In
            </Button>
            <Button onClick={() => setShowSurveyModal(true)} size="sm">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section with Orbit */}
      <section className="relative pt-6 pb-6 sm:pt-8 sm:pb-8 min-h-[40vh] sm:min-h-[50vh] flex items-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        
        {/* Amber Particle Embers Background - reduced on mobile */}
        <Suspense fallback={null}>
          <ParticleEmbers 
            count={20} 
            color="hsl(25 95% 53%)" 
            interactive={false}
            className="z-[1]"
          />
        </Suspense>
        
        {/* Animated background elements - hidden on mobile for performance */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-500" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Two-column layout - Text left, Orbit right */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
            {/* Text content - Left side */}
            <div className="text-center md:text-left order-2 md:order-1">
              <Badge variant="secondary" className="mb-4 gap-1.5">
                <Sparkles className="h-3 w-3" />
                AI-Powered Brand Platform
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight mb-4 md:mb-6">
                <span className="text-foreground">Create </span>
                <GlitchText text="Living" glowColor="hsl(25 95% 53%)" />
                <br />
                <GlitchText text="Brand" glowColor="hsl(25 95% 53%)" />
                <span className="text-foreground"> Guides</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-lg lg:text-xl text-muted-foreground mb-6 md:mb-8 max-w-xl mx-auto md:mx-0">
                Build smart, living guidelines for your brands, product lines, 
                and events — with built-in accessibility, cultural awareness, 
                and AI insights so everything works for everyone, everywhere.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center md:justify-start">
                <Button size="lg" onClick={() => setShowSurveyModal(true)} className="gap-2">
                  Start Building
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/demo/brand/brandhub')} className="gap-2 border-accent text-accent hover:bg-accent hover:text-accent-foreground">
                  <Play className="h-4 w-4" />
                  View Demo
                </Button>
              </div>

              {/* Stats - below CTA on left */}
              <div className="mt-8 md:mt-10 grid grid-cols-3 gap-4 md:gap-6 max-w-sm mx-auto md:mx-0">
                <div className="text-center md:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">{orbitBrands.length}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Demo Brands</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">{orbitProducts.length}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Products</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">{orbitEvents.length}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Events</div>
                </div>
              </div>
            </div>

            {/* Orbit Visualization - Right side - tablet optimized */}
            <div className="order-1 md:order-2 flex justify-center md:justify-end">
              <div className="w-full max-w-[320px] sm:max-w-[400px] md:max-w-[380px] lg:max-w-[520px] xl:max-w-[680px]">
                <HeroOrbit
                  className="w-full aspect-square"
                  primaryColor="#f07f1a"
                  centerLogo={brandhubLogo}
                  brands={orbitBrands}
                  products={orbitProducts}
                  events={orbitEvents}
                  onEntityClick={(entity) => {
                    // Map non-existent demo slugs to existing ones
                    const FALLBACK_SLUGS: Record<string, string> = {
                      'demo-pulse-media': 'brandhub',
                      'demo-horizon-finance': 'demo-nexus-tech',
                      'demo-pulse-stream': 'demo-nexus-cloud',
                      'demo-horizon-invest': 'demo-bloom-oils',
                      'demo-pulse-live': 'demo-innovation-summit',
                      'demo-finance-summit': 'demo-innovation-summit',
                      'demo-bloom-retreat': 'demo-innovation-summit',
                    };
                    const slug = entity.slug || entity.id;
                    const resolvedSlug = FALLBACK_SLUGS[slug] || slug;
                    const pathPrefix = entity.type === 'brand' ? '/demo/brand' : entity.type === 'product' ? '/demo/product' : '/demo/event';
                    // Use the fallback's correct type prefix
                    const fallbackType = FALLBACK_SLUGS[slug]
                      ? (['brandhub', 'demo-nexus-tech', 'demo-bloom-wellness'].includes(resolvedSlug) ? 'brand' : ['demo-nexus-cloud', 'demo-bloom-oils'].includes(resolvedSlug) ? 'product' : 'event')
                      : entity.type;
                    const prefix = fallbackType === 'brand' ? '/demo/brand' : fallbackType === 'product' ? '/demo/product' : '/demo/event';
                    const path = `${prefix}/${resolvedSlug}`;
                    handleDemoClick(path, entity.name);
                  }}
                />
                <p className="text-center text-[10px] md:text-xs text-muted-foreground mt-2 md:mt-3 opacity-70">
                  Click entities to explore • Hover for details
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Demos Section */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-muted/20 via-muted/40 to-muted/20 relative overflow-hidden" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 800px' }}>
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className="text-center mb-8 sm:mb-16">
            <Badge variant="secondary" className="mb-3 sm:mb-4 gap-1.5 animate-fade-in">
              <Sparkles className="h-3 w-3" />
              Interactive Demos
            </Badge>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-5">
              Explore <GlitchText text="Live" glowColor="hsl(25 95% 53%)" className="text-2xl sm:text-4xl lg:text-5xl" /> Examples
            </h2>
            <p className="text-base sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              See how brands, products, and events come to life with interactive guidelines. 
              Click any card to explore a fully-featured demo.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
            {featuredDemos.map((demo, index) => {
              const Icon = demo.icon;
              return (
                <ParallaxCard
                  key={demo.id}
                  onClick={() => handleDemoClick(demo.path, demo.name)}
                  imageSrc={demo.cardImage}
                  imageAlt={demo.name}
                  fallbackGradient={demo.gradient}
                  parallaxIntensity={15}
                  scaleOnHover={1.15}
                  priority={index === 0}
                  className="group rounded-3xl bg-card/80 backdrop-blur-sm border border-border/50 hover:border-accent/60 shadow-lg hover:shadow-2xl hover:shadow-accent/10"
                  imageClassName="h-36 sm:h-48"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.6s ease-out backwards',
                  }}
                >
                  {/* Icon with glow effect */}
                  <div className="absolute top-36 sm:top-48 -mt-14 right-4 z-10 p-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 group-hover:bg-accent/20 group-hover:border-accent/40 transition-all duration-500">
                    <Icon className="h-6 w-6 text-white drop-shadow-lg" />
                  </div>
                  
                  {/* Industry badge overlay */}
                  <div className="absolute top-4 left-4 z-10">
                    <Badge className="bg-black/40 backdrop-blur-md text-white border-white/20 text-xs font-medium">
                      {demo.industry}
                    </Badge>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4 sm:p-6 pt-3 sm:pt-4">
                    <h3 className="font-bold text-lg sm:text-xl mb-2 sm:mb-3 group-hover:text-accent transition-colors duration-300">
                      {demo.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {demo.description}
                    </p>
                    
                    <div className="flex items-center gap-2 mt-3 sm:mt-5 text-sm font-medium text-accent translate-x-0 group-hover:translate-x-1 transition-transform duration-300">
                      <span>Explore Demo</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl ring-1 ring-accent/30" />
                </ParallaxCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full Features Showcase - lazy loaded for faster TTI */}
      <Suspense fallback={<div className="min-h-[400px]" />}>
        <FeaturesShowcase />
      </Suspense>

      {/* Interactive CTA Section */}
      <Suspense fallback={<div className="min-h-[200px]" />}>
        <InteractiveCTA />
      </Suspense>

      {/* Footer */}
      <footer className="py-8 sm:py-12 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <BrandHubLogo size="sm" />
            
            {/* Footer Links */}
            <div className="flex items-center gap-6">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/knowledge')}
                className="text-muted-foreground hover:text-foreground gap-1.5"
              >
                <HelpCircle className="h-4 w-4" />
                FAQ
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/demo/brand/brandhub')}
                className="text-muted-foreground hover:text-foreground gap-1.5"
              >
                <Play className="h-4 w-4" />
                Demos
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Gas Alley Studios. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Get Started Survey Modal */}
      <Suspense fallback={null}>
        <GetStartedSurveyModal open={showSurveyModal} onOpenChange={setShowSurveyModal} />
      </Suspense>

      {/* Demo First Time Modal */}
      {pendingDemo && (
        <DemoFirstTimeModal
          open={showDemoModal}
          onOpenChange={setShowDemoModal}
          targetPath={pendingDemo.path}
          demoName={pendingDemo.name}
        />
      )}
    </div>
  );
};

export default Index;
